import * as PIXI from 'pixi.js';
import { Entity } from '../core/EntityManager';
import type { Vector2 } from '../core/Vector2';
import { getSprite } from '../content/SpriteCache';

export class Projectile extends Entity {
  private bulletSprite: PIXI.Sprite | null = null;
  private speed: number;
  private direction: Vector2;
  private maxDistance: number;
  private distanceTraveled = 0;
  private damage: number;

  constructor(
    x: number,
    y: number,
    direction: Vector2,
    speed: number,
    damage: number,
    maxDistance = 600,
  ) {
    super();
    this.radius = 4;
    this.speed = speed;
    this.direction = direction.clone().normalize();
    this.damage = damage;
    this.maxDistance = maxDistance;
    this.position.set(x, y);

    const texture = getSprite('bullet');
    if (texture) {
      this.bulletSprite = new PIXI.Sprite(texture);
      this.bulletSprite.anchor.set(0.5);
      this.bulletSprite.width = 16;
      this.bulletSprite.height = 16;
      this.bulletSprite.rotation = Math.atan2(this.direction.y, this.direction.x);
      this.sprite.addChild(this.bulletSprite);
    } else {
      this.drawFallback();
    }
  }

  private drawFallback(): void {
    const graphics = new PIXI.Graphics();
    graphics.circle(0, 0, 3);
    graphics.fill(0xffff00);
    graphics.circle(0, 0, 1.5);
    graphics.fill(0xffffff);
    this.sprite.addChild(graphics);
  }

  update(dt: number): void {
    const moveX = this.direction.x * this.speed * dt;
    const moveY = this.direction.y * this.speed * dt;
    this.position.x += moveX;
    this.position.y += moveY;
    this.distanceTraveled += Math.sqrt(moveX * moveX + moveY * moveY);

    this.sprite.x = this.position.x;
    this.sprite.y = this.position.y;

    if (this.distanceTraveled >= this.maxDistance) {
      this.active = false;
    }
  }

  getDamage(): number {
    return this.damage;
  }

  hasReachedMaxDistance(): boolean {
    return this.distanceTraveled >= this.maxDistance;
  }

  destroy(): void {
    this.active = false;
    if (this.sprite.parent) {
      this.sprite.parent.removeChild(this.sprite);
    }
    this.sprite.destroy({ children: true });
  }
}
